import { Alert, Button } from '@mui/material';
import { find } from 'lodash';
import moment from 'moment';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { merchantsApi, shopsApi, tradersApi, statisticsApi } from 'api';
import { QueryFilters } from 'components';
import { FilterDefinitionType, OrderType, QueryKey } from 'enums';
import {
  useCurrencies,
  usePartialQuery,
  usePeriodGuard,
  useQueryFilters,
  useShopsQuery,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, SummaryStatisticFilters } from 'types';
import { formUtils } from 'utils';

import { STATISTICS_MAX_PERIOD_DAYS } from '../statistics.constants';
import { SummaryCards } from '../SummaryCards';

type Props = {
  orderType: OrderType;
};

const getDefaultFrom = () =>
  moment().subtract(1, 'w').startOf('d').toISOString();

export const SummaryTab: React.FC<Props> = ({ orderType }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { assetCurrenciesOptions, defaultAssetCurrency, getAssetCurrencyCode } =
    useCurrencies();
  const { p2pProviders } = useUserContext();

  const { filters, setFilters } = useQueryFilters<SummaryStatisticFilters>({
    assetCurrencyId: defaultAssetCurrency?.id,
    from: getDefaultFrom(),
  });

  const { isPeriodTooWide, handleResetPeriod } = usePeriodGuard({
    filters,
    setFilters,
    maxPeriodDays: STATISTICS_MAX_PERIOD_DAYS,
    getDefaultFrom,
  });

  const { role, apiRole, isAdmin, isTechOperator, isTrader } = useUser();
  const canManage = useMemo(
    () => isAdmin || isTechOperator,
    [isAdmin, isTechOperator],
  );

  const queryResult = usePartialQuery(
    `${QueryKey.DailyRequisitesStatistic}-${orderType}`,
    statisticsApi.getSummary(orderType),
    { paginated: false, enabled: !isPeriodTooWide },
  );

  const queryResultTraders = useQuery(
    QueryKey.Traders,
    () => tradersApi.getAllAsRole(role)(),
    {
      enabled: canManage,
    },
  );

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    () => merchantsApi.getAllAsRole(role)(),
    {
      enabled: canManage,
    },
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(apiRole),
    { enabled: !isTrader },
  );

  const filtersDefinitions: FilterDefinition<SummaryStatisticFilters>[] =
    useMemo(
      () => [
        {
          label: tCommon('filters.trader'),
          name: 'traderId',
          type: FilterDefinitionType.Trader,
          traders: queryResultTraders.data,
          hidden: !canManage,
        },
        {
          label: tCommon('filters.merchant'),
          name: 'merchantId',
          type: FilterDefinitionType.Merchant,
          merchants: queryResultMerchants.data,
          hidden: !canManage,
        },
        {
          label: tCommon('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
          hidden: isTrader,
        },
        {
          label: tCommon('filters.p2p_provider'),
          name: 'p2pProviderId',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(p2pProviders),
          getDisplayName: (value: string) =>
            find(p2pProviders, { id: value })?.name,
          hidden: !canManage,
        },
        {
          label: tCommon('filters.asset_currency'),
          name: 'assetCurrencyId',
          type: FilterDefinitionType.Select,
          options: assetCurrenciesOptions,
          noneOption: false,
          removable: false,
          getDisplayName: getAssetCurrencyCode,
        },
      ],
      [
        tCommon,
        queryResultTraders.data,
        canManage,
        queryResultMerchants.data,
        queryResultShops.data,
        isTrader,
        assetCurrenciesOptions,
        getAssetCurrencyCode,
        p2pProviders,
      ],
    );

  return (
    <Fragment>
      <QueryFilters withCommon filtersDefinitions={filtersDefinitions} />
      {isPeriodTooWide && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleResetPeriod}>
              {tCommon('features.statistics.period_limit_action')}
            </Button>
          }
        >
          {tCommon('features.statistics.period_limit', {
            days: STATISTICS_MAX_PERIOD_DAYS,
          })}
        </Alert>
      )}
      <SummaryCards
        queryResult={queryResult}
        exclude={['tradersReferralsCompensation']}
      />
    </Fragment>
  );
};
